import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Character } from '../types';
import CharacterList from '../components/codex/CharacterList';
import CharacterDetail from '../components/codex/CharacterDetail';

const CodexPage: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  useEffect(() => {
    fetchCharacters();
  }, []);

  const fetchCharacters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/characters');
      const charactersData = response.data.characters || [];
      setCharacters(charactersData);
    } catch (err) {
      console.error('Failed to load characters:', err);
      setError('Failed to load characters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCharacter = (character: Character) => {
    setSelectedCharacter(character);
    setShowMobileDetail(true);
    // Smooth scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToList = () => {
    setShowMobileDetail(false);
  };

  if (loading) {
    return (
      <div className="codex-loading-container">
        <div className="codex-loading-content">
          <div className="loading-spinner"></div>
          <p>Loading character codex...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Codex</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchCharacters}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="codex-container">
      <div className="codex-header">
        <h1>Character Codex</h1>
        <p className="codex-subtitle">
          Browse and explore all {characters.length} characters
        </p>
      </div>

      <div className="codex-content">
        <div className={`codex-list-view ${showMobileDetail ? 'mobile-hidden' : ''}`}>
          <CharacterList
            characters={characters}
            selectedCharacter={selectedCharacter}
            onSelectCharacter={handleSelectCharacter}
          />
        </div>
        
        <div className={`codex-detail-view ${showMobileDetail ? 'mobile-visible' : ''}`}>
          {showMobileDetail && (
            <button 
              className="mobile-back-button"
              onClick={handleBackToList}
            >
              ← Back to List
            </button>
          )}
          <CharacterDetail character={selectedCharacter} />
        </div>
      </div>
    </div>
  );
};

export default CodexPage;
